<script setup>
import { onMounted, onUnmounted, ref, watch, computed, nextTick } from 'vue'
import { useResultsStore } from '../stores/results-fetcher.js'
import { useStateStore } from '../stores/state.js'
import { useRoute } from "vue-router";
import { formatGapTime } from '../utils/gap-formatter.js'
import EventSwitcher from './EventSwitcher.vue'

const resultsStore = useResultsStore()
const stateStore = useStateStore()
const route = useRoute()
const eventAlias = route.params.eventAlias
resultsStore.fetchResults(eventAlias)
resultsStore.fetchEventDetails(eventAlias)
stateStore.fetchEvents()

const now = ref(Date.now())
let refreshResultsTimer = setInterval(() => {
      resultsStore.fetchResults(route.params.eventAlias)
    }, 30000)
let timer = null

// Search functionality
const searchQuery = ref('')
const showDropdown = ref(false)
const selectedResultId = ref(null)

// Track recently changed rows by result.id and the timestamp of change
const recentlyChanged = ref({})

// Helper to compare results and mark changed rows
let lastResults = []

watch(
  () => resultsStore.results.map(r => ({ id: r.id, score: r.score, finish_time: r.finish_time, finish_time_gps: r.finish_time_gps, dnf: r.dnf, abs: r.abs, categoryPosition: r.categoryPosition })),
  (newResults) => {
    const nowTime = Date.now()
    // Skip check if lastResults is empty (first load)
    if (lastResults.length === 0) {
      lastResults = JSON.parse(JSON.stringify(newResults))
      return
    }
    newResults.forEach((r) => {
      const prev = lastResults.find(lr => lr.id === r.id)
      if (
        !prev ||
        prev.score !== r.score ||
        prev.finish_time !== r.finish_time ||
        prev.finish_time_gps !== r.finish_time_gps ||
        prev.dnf !== r.dnf ||
        prev.abs !== r.abs ||
        prev.categoryPosition !== r.categoryPosition
      ) {
        recentlyChanged.value[r.id] = nowTime
      }
    })
    lastResults = JSON.parse(JSON.stringify(newResults))
  },
  { immediate: true }
)

// Clean up highlights after 30 seconds
const CLEANUP_INTERVAL = 1000

onUnmounted(() => {
  if (timer) clearInterval(timer)
  if (refreshResultsTimer) clearInterval(refreshResultsTimer)
})

// Hide DNS (did not start) competitors from the basic results list
const visibleResults = computed(() => resultsStore.results.filter(r => !r.abs))

// Computed property for filtered results based on search
const filteredResults = computed(() => {
  if (!searchQuery.value.trim()) {
    return []
  }

  const query = searchQuery.value.trim().toLowerCase()
  return visibleResults.value.filter(result => {
    const nameMatch = result.name.toLowerCase().includes(query)
    const numberMatch = result.number.toString().includes(query)
    return nameMatch || numberMatch
  }).slice(0, 10) // Limit to 10 results for performance
})

// Watch search query to show/hide dropdown
watch(searchQuery, (newQuery) => {
  showDropdown.value = newQuery.trim().length > 0 && filteredResults.value.length > 0
})

// Function to scroll to and highlight a result
function selectResult(result) {
  selectedResultId.value = result.id
  searchQuery.value = ''
  showDropdown.value = false
  
  // Add highlight with timestamp
  recentlyChanged.value[result.id] = Date.now()
  
  // Scroll to the result
  nextTick(() => {
    const resultRow = document.querySelector(`tr[data-result-id="${result.id}"]`)
    if (resultRow) {
      resultRow.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
  })
}

// Function to handle clicking outside dropdown
function handleClickOutside(event) {
  if (!event.target.closest('.search-container')) {
    showDropdown.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  timer = setInterval(() => {
    now.value = Date.now()
    // Remove highlights older than 30 seconds
    const cutoff = Date.now() - 30 * 1000; // 30 seconds
    for (const id in recentlyChanged.value) {
      if (recentlyChanged.value[id] < cutoff) {
        delete recentlyChanged.value[id]
      }
    }
  }, CLEANUP_INTERVAL)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  if (timer) clearInterval(timer)
  if (refreshResultsTimer) clearInterval(refreshResultsTimer)
})


function calculateTimeFromStart(result) {
  const startTime = new Date(result.start_time)
  const elapsed = new Date(now.value - startTime)
  if (elapsed < 0) {
    return ''
  }
  return getHHMMSSString(elapsed)
}

function getHHMMSSString(date) {
  // Handle negative or zero elapsed time - don't display
  const timestamp = date instanceof Date ? date.getTime() : (typeof date === 'number' ? date : 0)
  if (timestamp <= 0) {
    return ''
  }
  const hours = Math.floor(timestamp / (1000 * 60 * 60))
  const dateObj = date instanceof Date ? date : new Date(timestamp)
  const minutes = String(dateObj.getUTCMinutes()).padStart(2, '0')
  const seconds = String(dateObj.getUTCSeconds()).padStart(2, '0')
  return `${hours}:${minutes}:${seconds}`
}

function getResultClass(result) {
  if (result.dnf) return 'badge bg-danger'
  if (result.abs) return 'badge bg-secondary'
  if (result.finish_time || result.finish_time_gps) return ''
  return 'result-upcoming'
}
</script>

<template>
  <div>
    <EventSwitcher />
    <h1>Wyniki</h1>
    
    <!-- Search functionality -->
    <div class="search-container mb-3">
      <div class="position-relative">
        <div class="search-input-wrapper">
          <svg class="search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" fill="currentColor"/>
          </svg>
          <input 
            type="text" 
            v-model="searchQuery" 
            class="form-control search-input" 
            placeholder="Wyszukaj zawodnika po numerze lub nazwie..."
            @focus="showDropdown = searchQuery.trim().length > 0 && filteredResults.length > 0"
          />
        </div>
        
        <!-- Search dropdown -->
        <div v-if="showDropdown" class="search-dropdown">
          <div 
            v-for="result in filteredResults" 
            :key="result.id"
            class="search-dropdown-item"
            @click="selectResult(result)"
          >
            <span class="badge bg-secondary me-2">{{ result.number }}</span>
            <span>{{ result.name }}</span>
          </div>
        </div>
      </div>
    </div>
    
    <div class="table-scroll">
      <table class="table table-striped">
        <thead>
          <tr>
            <th>#</th>
            <th>Zawodnik</th>
            <th v-if="resultsStore.shouldShowCategoryColumn()">kat.</th>
            <th v-if="resultsStore.shouldShowPointsColumn()">Punkty</th>
            <th>Start</th>
            <th>Meta</th>
            <th>Czas</th>
            <th>Strata</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(result, index) in visibleResults"
            :key="result.id"
            :data-result-id="result.id"
            :class="{ 'row-changed': recentlyChanged[result.id] }"
          >
            <td>{{ index + 1 }}</td>
            <td><span class="badge bg-secondary">{{ result.number }}</span> {{ result.name }}</td>
            <td v-if="resultsStore.shouldShowCategoryColumn()">
              {{ result.categoryPosition }}
            </td>
            <td v-if="resultsStore.shouldShowPointsColumn()">
              {{ result.pointScore || 0 }}
            </td>
            <td>{{ result.start_time ? new Date(result.start_time).toLocaleTimeString() : '' }}</td>
            <td>
              {{ result.finish_time ? new Date(result.finish_time).toLocaleTimeString() : (result.finish_time_gps ? new Date(result.finish_time_gps).toLocaleTimeString() : '')}}
            </td>
            <td>
              <span v-if="result.dnf || result.abs" :class="getResultClass(result)">{{ result.abs? "DNS" : "DNF" }}</span>
              <span v-else :class="getResultClass(result)">
                {{ result.finish_time ? result.timeScore : (result.finish_time_gps ? result.timeScore : (result.start_time ? calculateTimeFromStart(result) : '--:--:--')) }}
              </span>
            </td>
            <td class="gap-cell">
              <div v-html="formatGapTime(resultsStore.getGapToLeader(result)).replace('\n', '<br/>')"></div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.result-upcoming {
  font-style: italic;
}
.table {
  text-align: left;
}
.table-scroll {
  width: 100%;
  max-height: 80vh;
  overflow-x: auto;
  overflow-y: auto;
}

/* Sticky table header */
.table thead th {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f8f9fa; /* Bootstrap table header background */
}
.table.table-striped > tbody > tr.row-changed > td,
tbody tr.row-changed > td {
  background: #ebd0b2 !important;
  transition: background 1s;
  font-weight: bold !important;
  animation: highlight-row 1s linear;
}
@keyframes highlight-row {
  0% { background: #ffa754; }
  100% { background: #ebd0b2; }
}

/* Search functionality styles */
.search-container {
  max-width: 400px;
}

.search-input-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  pointer-events: none;
  z-index: 10;
}

.search-input {
  padding-left: 40px;
}

.search-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 0.375rem 0.375rem;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.search-dropdown-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid #f8f9fa;
  display: flex;
  align-items: center;
}

.search-dropdown-item:hover {
  background-color: #f8f9fa;
}

.search-dropdown-item:last-child {
  border-bottom: none;
}

.gap-cell {
  white-space: pre-line;
  font-size: 0.9em;
}
</style>